import React, { useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useFlightPurchaseDetails from 'hooks/Flights/useFlightPurchaseDetails'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { useAppDispatch } from 'hooks/reduxHooks'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

export default function LesaStandaloneFlightPurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, canEdit } = props
  const intl = useIntl()

  const dispatch = useAppDispatch()

  const {
    fetching,
    flightsName,
    departingDate,
    arrivalDate,
    noOfAdults,
    noOfChildren,
    noOfInfants,
    flightTypeLabel,
    pnrIds,
  } = useFlightPurchaseDetails(order)

  const isListFlightsOrdersAction = !!componentsMap.get('list-flight-orders')
  const itemId = order.flightItems[0].id

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: isListFlightsOrdersAction ? 'selectFlightOrder' : 'selectOrder', payload: { id_orders: order.id, itemId, canEditOrder: canEdit } }))
  }, [dispatch, isListFlightsOrdersAction, itemId, order.id, canEdit])

  if (fetching) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['changeDates', 'changeRoom', 'listInclusions', 'showFinePrint', 'resendConfirmationEmail']))

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('list-flight-orders') || !!componentsMap?.get('selectFlightOrder') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LinePlaneDepartureIcon size="XS" />}>{intl.formatMessage({
              id: 'fN1UpH-pcaJdqCQg_75x',
              defaultMessage: 'Flights',
              description: 'Category label for standalone flight purchase in the flights section',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'e7wPbQ5mnoKI1jYHZvK4',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming flight purchase in the flights flow',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'VnlYb_rvZy_b4NCUq0Z2',
              defaultMessage: 'Past',
              description: 'Status label for past flight purchase in the flight search or booking flow',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {flightsName}
              </LesaPurchaseSummaryTitle>
              <BodyTextBlock variant="small" colour="neutral-two">
                <FormattedMessage
                  id="RzmNJu-J7JMgoikpN73x"
                  defaultMessage="{departDate} - {arriveDate}"
                  description="Flight departure and arrival date range in the flight search or booking flow"
                  values={{
                    departDate: moment(departingDate).format(DMY_CASUAL_SHORT_FORMAT),
                    arriveDate: moment(arrivalDate).format(DMY_CASUAL_SHORT_FORMAT),
                  }}
                />
              </BodyTextBlock>
              <LesaPurchaseSummarySeparator>
                <BodyText as="span" variant="small" colour="neutral-three">{formatOccupantsShort({
                  adults: noOfAdults,
                  children: noOfChildren,
                  infants: noOfInfants,
                })}
                </BodyText>
                <BodyText as="span" variant="small" colour="neutral-three">{flightTypeLabel}</BodyText>
              </LesaPurchaseSummarySeparator>
              {!!pnrIds?.length && <BodyTextBlock variant="small" colour="neutral-two">
                <FormattedMessage
                  id="HGyjmCUdYG8RW632Y-WJ"
                  defaultMessage="<strong>Booking ID:</strong> {bookingIds}"
                  description="Booking ID label and value for flight purchase in the flights section"
                  values={{
                    bookingIds: pnrIds.join(', '),
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </BodyTextBlock>}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'PjGQzKsyzuDGX7Al5JqP',
                  defaultMessage: 'Select',
                  description: 'Button to select a flight purchase in the flight search or booking flow',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}
